import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUGGESTIONS = {
  super_admin: ["Summarise platform activity", "What needs my approval?", "Which partners are most active?"],
  client: ["Help me write a JD for a React developer", "Explain the RFQ workflow", "Summarise my candidate pipeline"],
  vendor: ["How do I submit a strong quotation?", "Explain my RFQ invitations", "What does 'under review' mean?"],
  staffing: ["Summarise my submissions", "How does BGV work?", "Tips to improve selection ratio"],
};

export default function VsAI() {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [messages, setMessages] = React.useState([
    { role: "ai", text: "Hi, I'm VS AI — your role-aware business assistant. Ask me about your requirements, RFQs, candidates, workflows or analytics." },
  ]);
  const sessionRef = React.useRef(null);
  const endRef = React.useRef(null);

  React.useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: q, session_id: sessionRef.current });
      sessionRef.current = data.session_id || sessionRef.current;
      setMessages((m) => [...m, { role: "ai", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "VS AI is temporarily unavailable. Please try again shortly." }]);
    } finally { setLoading(false); }
  };

  const suggestions = SUGGESTIONS[user?.role] || [];

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="vs-ai-launcher"
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full vs-gradient shadow-lg flex items-center justify-center text-white hover:scale-105 transition-transform">
        <Sparkles className="h-6 w-6" />
      </button>
      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div initial={{ x: 440 }} animate={{ x: 0 }} exit={{ x: 440 }} transition={{ type: "spring", damping: 26, stiffness: 240 }}
              className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[420px] bg-white border-l border-slate-200 shadow-2xl flex flex-col" data-testid="vs-ai-panel">
              <div className="vs-gradient px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="h-9 w-9 rounded-lg bg-white/15 flex items-center justify-center"><Bot className="h-5 w-5 text-white" /></div>
                  <div><div className="font-display font-bold text-white">VS AI</div><div className="text-[11px] text-blue-200">Enterprise business assistant</div></div>
                </div>
                <button onClick={() => setOpen(false)} data-testid="vs-ai-close" className="text-white/80 hover:text-white"><X className="h-5 w-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-vs-royal text-white" : "bg-slate-100 text-slate-800"}`}>{m.text}</div>
                  </div>
                ))}
                {loading && <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> VS AI is thinking…</div>}
                <div ref={endRef} />
              </div>
              {messages.length <= 1 && (
                <div className="px-4 pb-2 flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => send(s)} className="text-xs rounded-full border border-slate-200 px-3 py-1.5 text-slate-600 hover:border-vs-royal hover:text-vs-royal transition-colors">{s}</button>
                  ))}
                </div>
              )}
              <div className="p-4 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Ask VS AI…" data-testid="vs-ai-input"
                    className="flex-1 rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-vs-royal" />
                  <Button onClick={() => send()} disabled={loading} className="bg-vs-royal text-white h-10 w-10 p-0" data-testid="vs-ai-send"><Send className="h-4 w-4" /></Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
